import { useEffect, createContext, type PropsWithChildren, useCallback, useState } from 'react';

import axios from 'axios';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { GET_USER_DATA } from '../../config/apiPaths';
import { getUrls, processApiPath } from '../../utils/url';

export interface PlannerProviderProps {
    plannerId: string | null;
    plannerUuid: string;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface PlannerContextInterface {
    plannerId: string | null;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    errorMessage: string;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    plannerData: any;
    setPlannerData: (plannerData: any) => void;
    onBack?: () => void;
    me: any;
    meFetched: boolean;
}

const PlannerContext = createContext(undefined as unknown as PlannerContextInterface);

const { Provider, Consumer } = PlannerContext;

const PlannerProvider = (props: PropsWithChildren<PlannerProviderProps>) => {
    const search = new URLSearchParams(window.location.search);
    const plannerUuid = search.get('planner_uuid') || window.localStorage.getItem('planner_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { fullScreen, narrowStyle, plannerId, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [plannerData, setPlannerData] = useState<any>({ loading: true, plannerData: {} });
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [me, setMe] = useState<any>({});
    const [meFetched, setMeFetched] = useState<boolean>(false);
    const { api } = getUrls();

    console.log('plannerProvider plannerId', plannerId);

    const fetchPlannerData = useCallback(async () => {
        try {
            // should get extra data not base data
            const { data } = await axios.get(
                `${api}${GET_USER_DATA}?planner_id=${plannerId}&planner_uuid=${plannerUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );
            setPlannerData(data);
            setIsLoading(false);
        } catch (err) {
            setErrorMessage(err!.toString());
            setPlannerData(undefined);
            setIsLoading(false);
        }
    }, [api, plannerId, authMode, plannerUuid, showDemoContent]);

    useEffect(() => {
        if (!plannerUuid || !plannerId) {
            return;
        }

        const fetchMe = async () => {
            const getMeResponse = await axios.get(
                processApiPath(GET_USER_DATA, undefined, {
                    planner_uuid: plannerUuid,
                    planner_id: plannerId,
                    auth_mode: authMode
                })
            );
            setMe(getMeResponse);
            setMeFetched(true);
            fetchPlannerData();
        };

        fetchMe();
    }, [api, plannerId, fetchPlannerData, authMode, plannerUuid]);

    return (
        <Provider
            value={{
                plannerId,
                loading: plannerData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                plannerData,
                setPlannerData,
                me,
                meFetched,
                errorMessage
            }}
        >
            {children}
        </Provider>
    );
};

export { PlannerContext, PlannerProvider, Consumer as PlannerConsumer };
